import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import PageHeader from 'shared/components/page-header/page-header';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';

import ResidentChangeLog from 'features/residents/models/resident-change-log';
import ResidentsChangeLogProviderContext from 'features/residents/resident-change-logs/providers/contexts/residents-change-log-provider-context';
import CategoryColumn from 'features/residents/resident-change-logs/views/components/columns/category-column/category-column';
import ResidentOrganisationUnitNameColumn from 'features/residents/resident-change-logs/views/components/columns/resident-organisation-unit-name-column/resident-organisation-unit-name-column';
import UserColumn from 'features/residents/resident-change-logs/views/components/columns/user-column/user-column';
import ResidentsChangeLogFilters from 'features/residents/resident-change-logs/views/components/residents-change-log-filters/residents-change-log-filters';
import styles from 'features/residents/resident-change-logs/views/residents-change-log.module.scss';
import ResidentsNavigation from 'features/residents/resident-navigation/views/residents-navigation';

const ResidentsChangeLog = (): React.JSX.Element => {
  const { t } = useTranslation('residentsChangeLog');

  const { residentsChangeLog, residentsChangeLogIsLoading, table } = use(ResidentsChangeLogProviderContext);

  const lastUpdate = residentsChangeLog?.lastUpdatedOn ? ` ${moment(residentsChangeLog.lastUpdatedOn).format('DD-MM-YYYY HH:mm')}` : ` -`;

  const nextUpdate = residentsChangeLog?.nextUpdateOn ? ` ${moment(residentsChangeLog.nextUpdateOn).format('DD-MM-YYYY HH:mm')}` : ` -`;

  const columns: ColumnDefinition<ResidentChangeLog, NestedKeyof<ResidentChangeLog>>[] = [
    {
      key: 'organisationUnitName',
      label: t('residentsChangeLogTable.column.resident'),
      renderCustomContentProvider: (residentSetting) => (
        <ResidentOrganisationUnitNameColumn
          organisationUnitName={residentSetting.organisationUnitName}
          organisationUnitAncestors={residentSetting.organisationUnitAncestors}
        />
      ),
      disableSort: true,
    },
    {
      key: 'category',
      label: t('residentsChangeLogTable.column.category'),
      renderCustomContentProvider: (item) => <CategoryColumn item={item} />,
      disableSort: true,
    },
    {
      key: 'type',
      label: t('residentsChangeLogTable.column.edit'),
      renderCustomContentProvider: (item) => <Typography variant="subtitle1">{t(`residentsChangeLogTable.type.${item.type}`)}</Typography>,
      disableSort: true,
    },
    {
      key: 'oldValueDisplay',
      label: t('residentsChangeLogTable.column.from'),
      renderCustomContentProvider: (item) => (
        <Typography variant="subtitle1" sx={{ whiteSpace: 'pre-wrap' }}>
          {item.oldValueDisplay}
        </Typography>
      ),
      disableSort: true,
      tableCellProps: {
        width: '20%',
      },
    },
    {
      key: 'newValueDisplay',
      label: t('residentsChangeLogTable.column.to'),
      renderCustomContentProvider: (item) => (
        <Typography variant="subtitle1" sx={{ whiteSpace: 'pre-wrap' }}>
          {item.newValueDisplay}
        </Typography>
      ),
      disableSort: true,
      tableCellProps: {
        width: '20%',
      },
    },
    {
      key: 'time',
      label: t('residentsChangeLogTable.column.time'),
      renderCustomContentProvider: (item) => <>{moment(item.time).format('DD-MM-YYYY · HH:mm:ss').toString()}</>,
      disableSort: true,
    },
    {
      key: 'userFullName',
      label: t('residentsChangeLogTable.column.user'),
      renderCustomContentProvider(item) {
        return <UserColumn userFullName={item.userFullName} />;
      },
      disableSort: true,
    },
  ];

  return (
    <div className={styles.container}>
      <PageHeader
        title={t('pageTitle')}
        descriptionComponent={
          <>
            <Typography variant="subtitle1">
              {t('lastUpdated')}
              {lastUpdate}
            </Typography>
            <Typography variant="subtitle1">
              {t('nextUpdated')}
              {nextUpdate}
            </Typography>
          </>
        }
        navigationComponent={<ResidentsNavigation />}
        filterComponent={<ResidentsChangeLogFilters />}
      />
      {residentsChangeLogIsLoading && <LoadingIndicator />}

      {!residentsChangeLogIsLoading && (
        <Table
          data={residentsChangeLog?.changeLogs.map<RowItem<ResidentChangeLog>>((changeLog) => ({ data: changeLog })) ?? []}
          columns={columns}
          pagination={{
            count: residentsChangeLog?.total ?? 0,
            onPageChanged: table.handleOnPageChanged,
            rowsPerPage: table.currentRowsPerPage,
            page: table.currentPage,
            onRowsPerPageChanged: table.handleOnRowsPerPageChanged,
          }}
          sx={{ tableLayout: 'fixed' }}
          rowIdentifier={(item: ResidentChangeLog) => item.id}
        />
      )}
    </div>
  );
};

export default ResidentsChangeLog;
